/**
 * React Query keys for workspace members.
 * One root (`workspace-members`); purpose / search / resolve are suffixes — never
 * parallel roots like `workspace-members-list`.
 */
export type WorkspaceMembersRosterPurpose =
  | 'leads-filters'
  | 'lead-timeline'
  | 'assignee-lock'
  | 'event-roster'
  | 'event-details'
  | 'event-analytics'
  | 'products-list'
  | 'dashboard-top-cards';

export const workspaceMembersQueryKeys = {
  /** Prefix: all member caches. */
  root: ['workspace-members'] as const,
  all: (workspaceId: string) => ['workspace-members', workspaceId] as const,
  /** Full roster fetched for a UI surface (IDs only — never workspace objects). */
  roster: (workspaceId: string, purpose: WorkspaceMembersRosterPurpose) =>
    ['workspace-members', workspaceId, purpose] as const,
  /** Searchable picker list (replaces former `workspace-members-list` root). */
  list: (workspaceId: string, search: string) =>
    ['workspace-members', workspaceId, 'list', search] as const,
  /** Member search for pickers (replaces `workspace-members-search`). */
  search: (workspaceId: string | undefined, search: string) =>
    ['workspace-members', workspaceId, 'search', search] as const,
  /** Resolve a single user id to a member row (replaces `workspace-member-resolve`). */
  resolve: (workspaceId: string | undefined, userId: string) =>
    ['workspace-members', workspaceId, 'resolve', userId] as const,
} as const;

/** Teams keys colocated with member roster pickers (lead events). */
export type TeamsRosterPurpose = 'event-roster' | 'event-details';

export const teamsQueryKeys = {
  root: ['teams'] as const,
  all: (workspaceId: string) => ['teams', workspaceId] as const,
  roster: (workspaceId: string, purpose: TeamsRosterPurpose) =>
    ['teams', workspaceId, purpose] as const,
  members: (workspaceId: string, teamId: string) =>
    ['teams', workspaceId, 'team-members', teamId] as const,
} as const;
